/**
 * orobas.app
 *
 * Three routes, three languages, one account, one licence. The page is the product's
 * front door: it says what the two apps are, takes the sign-in, and takes the payment.
 */

import React from 'react';

import { AuthProvider } from './auth/AuthProvider';
import { AccountView, OrderStatusView } from './components/AccountView';
import { DesktopCallback } from './components/DesktopCallback';
import { OAuthCallback } from './components/OAuthCallback';
import { Footer, Navbar } from './components/Chrome';
import { Home } from './components/Home';
import { PricingView } from './components/PricingView';
import { SkinNFTView } from './components/SkinNFTView';
import { LocaleHead, useI18n } from './i18n';
import { Router, useRouter } from './router';

/*
 * `useAuth` is deliberately not called here. This component only decides which
 * view to show, and the views read the account themselves — an unused call here
 * would have been a second place that has to be kept in step with the provider.
 *
 * `useI18n` is called here for the skip link, and for nothing else. A skip link is
 * the first thing a keyboard user reaches and the last thing anybody checks, so it
 * being right matters more than it looks.
 */

const App: React.FC = () => {
  const { route, orderId } = useRouter();
  const { t } = useI18n();

  return (
    <div className="flex min-h-screen flex-col">
      {/*
        `<LocaleHead>` sets `<html lang>`, the title, the descriptions and the
        `hreflang` set from the address bar. It renders nothing, and it has to sit
        inside the router so it reads the language the same way everything else does.
      */}
      <LocaleHead />

      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-gold focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-ink"
      >
        {t.chrome.skipToContent}
      </a>

      <Navbar />

      <main id="conteudo" className="flex-1">
        {/*
          The order view is reachable at any time with an id in the query, because
          that is where the payment provider sends people. It is checked before the
          route so `/conta/obrigado?order=…` shows the payment state rather than the
          account page — and it renders for a signed-out visitor too, who is
          redirected to sign in by the account view itself.
        */}
        {orderId !== null && orderId !== '' ? (
          <OrderStatusView orderId={orderId} />
        ) : route === 'auth-callback' ? (
          /*
            Checked before the account page because it is a *destination*, not a
            place the reader browses to: a Google sign-in started from one of the
            desktop apps redirects here, and the navbar's "Entrar" would otherwise
            offer to sign them into this browser as well — a session they did not ask
            for, on a machine they may not even be sitting at.
          */
          <DesktopCallback />
        ) : route === 'oauth-callback' ? (
          <OAuthCallback />
        ) : route === 'conta' ? (
          <AccountView />
        ) : route === 'planos' ? (
          <PricingView />
        ) : route === 'skinNFT' ? (
          <SkinNFTView />
        ) : (
          <Home />
        )}
      </main>

      <Footer />
    </div>
  );
};

const Root: React.FC = () => (
  <Router>
    <AuthProvider>
      <App />
    </AuthProvider>
  </Router>
);

export default Root;
